import React from 'react'
import { ResumeItem } from '../../services/apiService'
import { ResumeFitAnalysisResponse, TailoredResumeResponse } from '../../services/jobApplicationService'
import './Step4Confirmation.css'

interface Step4ConfirmationProps {
  fitAnalysis: ResumeFitAnalysisResponse | null
  availableResumes: ResumeItem[]
  tailoredResume: TailoredResumeResponse | null
  selectedResumeId: string | null
  selectionType: 'best-fit' | 'saved' | 'custom'
  bestFitResumeId: string | null
  onSave: () => Promise<void>
  onBack: () => void
}

export const Step4Confirmation: React.FC<Step4ConfirmationProps> = ({
  fitAnalysis,
  availableResumes,
  tailoredResume,
  selectedResumeId,
  selectionType,
  bestFitResumeId,
  onSave,
  onBack,
}) => {
  const [isSaving, setIsSaving] = React.useState(false)

  // Determine which resume is selected
  const selectedResume = React.useMemo(() => {
    if (selectionType === 'custom' && selectedResumeId?.startsWith('tailored-')) {
      return availableResumes.find(r => r.id === selectedResumeId)
    }
    if (selectionType === 'custom' && tailoredResume) {
      return availableResumes.find(r => r.id === tailoredResume.baseResumeId)
    }
    if (selectionType === 'best-fit' && bestFitResumeId) {
      return availableResumes.find(r => r.id === bestFitResumeId)
    }
    if (selectionType === 'saved' && selectedResumeId) {
      return availableResumes.find(r => r.id === selectedResumeId)
    }
    return null
  }, [selectionType, selectedResumeId, bestFitResumeId, tailoredResume, availableResumes])

  // Get fit score for selected resume
  const fitScore = React.useMemo(() => {
    if (!fitAnalysis || !selectedResume) return null
    const analysis = fitAnalysis.resumeFitAnalyses?.find(a => a.resumeId === selectedResume.id)
    return analysis?.fitScore || null
  }, [fitAnalysis, selectedResume])

  const getFitScoreColor = (score: number): string => {
    if (score >= 80) return 'excellent'
    if (score >= 60) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const getFitScoreLabel = (score: number): string => {
    if (score >= 80) return 'Excellent Match'
    if (score >= 60) return 'Good Match'
    if (score >= 40) return 'Moderate Match'
    return 'Low Match'
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave()
    } catch (error) {
      // Error handling is done in parent component
      console.error('Failed to save resume selection:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="step4-confirmation">
      <div className="step-header">
        <h3>Confirm Selection</h3>
        <p className="step-description">
          Review your resume selection and confirm to save it to this job application.
        </p>
      </div>

      <div className="step4-selection-summary">
        <div className="step4-summary-card">
          <div className="step4-summary-header">
            <h4>Selected Resume</h4>
            {selectionType === 'custom' && (
              <span className="step4-selection-type-badge custom">Custom Tailored</span>
            )}
            {selectionType === 'saved' && (
              <span className="step4-selection-type-badge saved">Saved Resume</span>
            )}
            {selectionType === 'best-fit' && (
              <span className="step4-selection-type-badge best-fit">Best Fit</span>
            )}
          </div>
          <div className="step4-summary-body">
            {selectedResume ? (
              <>
                <p className="step4-resume-name">
                  {selectedResume.originalFilename || selectedResume.filename || 'Resume'}
                </p>
                {fitScore !== null && (
                  <div className={`step4-fit-score ${getFitScoreColor(fitScore)}`}>
                    <span className="step4-fit-score-value">{Math.round(fitScore)}%</span>
                    <span className="step4-fit-score-label">{getFitScoreLabel(fitScore)}</span>
                  </div>
                )}
                {selectionType === 'custom' && tailoredResume && (
                  <div className="step4-custom-details">
                    <p className="step4-custom-note">
                      This is a custom tailored resume generated specifically for this job application.
                    </p>
                    {tailoredResume.wizardPreferences && (
                      <p className="step4-custom-settings">
                        Generated with: {tailoredResume.wizardPreferences.tones
                          .map(t => t.charAt(0).toUpperCase() + t.slice(1))
                          .join(' + ')} tone, {tailoredResume.wizardPreferences.length} length
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="step4-no-resume">No resume selected</p>
            )}
          </div>
        </div>
      </div>

      <div className="step4-info-box">
        <p>
          <strong>Note:</strong> This selection will be saved to the job application. 
          You can change it later by opening the resume selection sidebar again.
        </p>
      </div>

      <div className="step-actions">
        <button 
          className="btn btn-secondary" 
          onClick={onBack}
          disabled={isSaving}
        >
          ← Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving || !selectedResume}
        >
          {isSaving ? 'Saving...' : 'Save Assignment'}
        </button>
      </div>
    </div>
  )
}

